import { create } from 'zustand';

import type { EntityIdentifier } from '../../../data-contract/chat';

/** An updated item the agent pushed; it opens at its new version if the user is looking at it. */
export type VersionRedirect = {
    /** When the refresh arrived; the match window runs from here, not from any later mount. */
    readonly arrivedAt: number;
    readonly entity: EntityIdentifier;
    readonly key: string;
    /** Set once it matched a visible item; from then on it only waits for its navigation. */
    readonly matchedAt: number | null;
    /** Set once it opened the item; it stays until the version refetches are done too. */
    readonly navigatedAt: number | null;
};

// Settled redirects are only offered again by a redelivered refresh, which comes right after the
// first one, so the most recent ones are enough to keep each redirect to a single navigation.
export const MAX_SETTLED_REDIRECTS = 200;

type VersionRedirectState = {
    readonly redirects: readonly VersionRedirect[];
    /** Each event and entity redirects at most once per session, however often it is offered again. */
    readonly settledKeys: ReadonlySet<string>;
    readonly markMatched: (key: string, matchedAt: number) => void;
    readonly markNavigated: (key: string, navigatedAt: number) => void;
    readonly offer: (redirects: readonly VersionRedirect[]) => void;
    /** Drops a redirect for good: navigated, expired or given up. */
    readonly settle: (key: string) => void;
};

export const useVersionRedirectStore = create<VersionRedirectState>((set) => ({
    markMatched: (key, matchedAt) =>
        set((state) => ({
            redirects: state.redirects.map((redirect) =>
                redirect.key === key ? { ...redirect, matchedAt } : redirect,
            ),
        })),
    markNavigated: (key, navigatedAt) =>
        set((state) => ({
            redirects: state.redirects.map((redirect) =>
                redirect.key === key ? { ...redirect, navigatedAt } : redirect,
            ),
        })),
    offer: (redirects) =>
        set((state) => {
            const known = new Set(state.redirects.map((redirect) => redirect.key));
            const fresh = redirects.filter(
                (redirect) => !known.has(redirect.key) && !state.settledKeys.has(redirect.key),
            );
            return fresh.length === 0 ? state : { redirects: [...state.redirects, ...fresh] };
        }),
    redirects: [],
    settle: (key) =>
        set((state) => ({
            redirects: state.redirects.filter((redirect) => redirect.key !== key),
            settledKeys: new Set([...state.settledKeys, key].slice(-MAX_SETTLED_REDIRECTS)),
        })),
    settledKeys: new Set(),
}));

const versionRedirectKey = (eventId: string, index: number): string => `${eventId}-${index}`;

export const offerVersionRedirects = (
    eventId: string,
    entities: readonly EntityIdentifier[],
    arrivedAt: number,
): void => {
    useVersionRedirectStore.getState().offer(
        entities.map((entity, index) => ({
            arrivedAt,
            entity,
            key: versionRedirectKey(eventId, index),
            matchedAt: null,
            navigatedAt: null,
        })),
    );
};
